import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

/**
 * One-time, non-subscription flat-price checkout for the **New Patient Deal**.
 * - Signed-out users → bounce to /register?promo=new-patient
 * - Signed-in first-time patients → 2-step dialog (drug picker + attestation)
 *   → POST /api/payments/new-patient-deal → redirect to hosted checkout URL.
 *
 * Prices are hard-wired in `loyalty_rules.py` and fetched via
 * `/api/loyalty-rules/public`, so the dialog always shows the live admin-
 * editable price per drug (Sema $79.99, Tirz $99.99 as defaults). The deal
 * is non-stackable: it never passes through the coupon stack, it's a flat-
 * price hosted checkout.
 *
 * Props:
 *   amount: number (fallback display price when loyalty-rules can't load)
 *   variant: "inline-cta" (green CTA button only) | "full" (card shell)
 *   presetDrug: initial drug picker value — the dialog still lets the
 *     patient flip between Sema and Tirz before checkout.
 */
export default function NewPatientDealCard({ amount = 79.99, variant = "inline-cta", presetDrug = "semaglutide" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [drug, setDrug] = useState(presetDrug);
  const [attested, setAttested] = useState(false);
  const [loading, setLoading] = useState(false);
  // Hard-wired defaults — if the public rules endpoint 404s or hiccups
  // we still show the correct per-drug price.
  const [prices, setPrices] = useState({ semaglutide: amount || 79.99, tirzepatide: 99.99 });

  // Keep the selected medication in sync with the parent dropdown.
  useEffect(() => { setDrug(presetDrug); }, [presetDrug]);

  // Pull live admin-editable prices once the dialog opens so admins can
  // tweak the number in loyalty_rules without a redeploy.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await API.get("/loyalty-rules/public");
        if (cancelled) return;
        setPrices({
          semaglutide: Number(data?.new_patient_price) || 79.99,
          tirzepatide: Number(data?.new_patient_price_tirz) || 99.99,
        });
      } catch { /* keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const selectedPrice = Number(prices[drug] || 0).toFixed(2);

  const start = () => {
    if (!user) {
      navigate(`/register?promo=new-patient&drug=${drug}`);
      return;
    }
    setOpen(true);
    setAttested(false);
  };

  const submit = async () => {
    if (!attested) {
      toast.error("Please confirm the first-time patient attestation to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await API.post("/payments/new-patient-deal", {
        drug,
        first_time_attestation: true,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        try {
          const { analytics } = await import("@/instrument");
          analytics.capture("checkout_started", {
            medication_slug: drug,
            dose_tier: "starter",
            variant: "new_patient_deal",
          });
        } catch (_e) { /* noop */ }
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
    } finally {
      setLoading(false);
    }
  };

  // Public-facing variant: shell card that advertises BOTH drugs with
  // their hard-wired flat prices next to each other.
  if (variant === "full") {
    return (
      <>
        <div className="rounded-2xl border border-teal-200 dark:border-teal-800 bg-gradient-to-br from-teal-50 to-emerald-50 dark:from-teal-950/40 dark:to-emerald-950/40 p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-teal-700 dark:text-teal-300">
            New Patient · First Time Only · One-Time
          </p>
          <h3 className="mt-1 text-lg font-bold text-slate-900 dark:text-white">Pick your starter dose</h3>
          <div className="mt-3 grid grid-cols-2 gap-2" data-testid="new-patient-deal-strip">
            {[
              { k: "semaglutide", label: "Semaglutide", dose: "0.25 mg starter", price: prices.semaglutide },
              { k: "tirzepatide", label: "Tirzepatide", dose: "2.5 mg starter", price: prices.tirzepatide },
            ].map((p) => (
              <button
                key={p.k}
                type="button"
                onClick={() => { setDrug(p.k); start(); }}
                data-testid={`new-patient-deal-pick-${p.k}`}
                className="group rounded-xl border-2 border-slate-200 dark:border-slate-700 hover:border-teal-400 bg-white dark:bg-slate-900 p-3 text-left transition"
              >
                <p className="text-sm font-semibold text-slate-900 dark:text-white">{p.label}</p>
                <p className="text-[11px] text-slate-500 dark:text-slate-400">{p.dose}</p>
                <p className="mt-1 text-base font-bold text-teal-700 dark:text-teal-300">
                  ${Number(p.price).toFixed(2)}
                </p>
              </button>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
            One-time, non-subscription. Non-stackable with other coupons.
          </p>
        </div>
        {/* Dialog is still rendered so the small-variant branch and the full-
            variant branch share the same checkout flow. */}
        {open && (
          <DealDialog
            open={open}
            setOpen={setOpen}
            drug={drug} setDrug={setDrug}
            prices={prices}
            selectedPrice={selectedPrice}
            attested={attested} setAttested={setAttested}
            loading={loading}
            submit={submit}
          />
        )}
      </>
    );
  }

  return (
    <>
      <Button
        className="bg-white hover:bg-slate-100 text-teal-700 font-semibold rounded-full px-5"
        onClick={start}
        data-testid="new-patient-deal-start-btn"
      >
        Starter dose from ${Number(prices[presetDrug] || prices.semaglutide).toFixed(2)} <ArrowRight className="w-4 h-4 ml-1" />
      </Button>

      <DealDialog
        open={open}
        setOpen={setOpen}
        drug={drug} setDrug={setDrug}
        prices={prices}
        selectedPrice={selectedPrice}
        attested={attested} setAttested={setAttested}
        loading={loading}
        submit={submit}
      />
    </>
  );
}


function DealDialog({ open, setOpen, drug, setDrug, prices, selectedPrice, attested, setAttested, loading, submit }) {
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setOpen(false); }}>
      <DialogContent className="max-w-md" data-testid="new-patient-deal-dialog">
        <DialogHeader>
          <DialogTitle>New Patient Deal · ${selectedPrice}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            One-time, non-subscription starter dose at the hard-wired first-time price. Non-stackable with any other coupon.
          </p>
          <div className="grid grid-cols-2 gap-2">
            {[
              { k: "semaglutide", label: "Semaglutide", dose: "0.25 mg · starter" },
              { k: "tirzepatide", label: "Tirzepatide", dose: "2.5 mg · starter" },
            ].map((opt) => {
              const price = Number(prices[opt.k] || 0).toFixed(2);
              const active = drug === opt.k;
              return (
                <button
                  key={opt.k}
                  type="button"
                  onClick={() => setDrug(opt.k)}
                  className={`text-left p-3 rounded-xl border-2 transition ${active
                    ? "border-teal-500 bg-teal-50 dark:bg-teal-900/20"
                    : "border-slate-200 dark:border-slate-700 hover:border-slate-400"}`}
                  data-testid={`new-patient-drug-${opt.k}`}
                >
                  <p className="font-semibold text-sm text-slate-900 dark:text-white">{opt.label}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{opt.dose}</p>
                  <p className={`mt-1 text-sm font-bold ${active ? "text-teal-700 dark:text-teal-300" : "text-slate-700 dark:text-slate-200"}`}>
                    ${price}
                  </p>
                </button>
              );
            })}
          </div>
          <label className="flex items-start gap-2.5 p-3 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 cursor-pointer">
            <input
              type="checkbox"
              checked={attested}
              onChange={(e) => setAttested(e.target.checked)}
              className="mt-0.5"
              data-testid="new-patient-attestation-checkbox"
            />
            <span className="text-xs text-amber-900 dark:text-amber-200 leading-relaxed">
              I confirm this is a <strong>first-time patient price</strong> on a <strong>one-time non-subscription order</strong> for a <strong>single starter-dose vial only</strong>. Non-stackable with any other coupon.
            </span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={submit}
            disabled={!attested || loading}
            className="bg-teal-600 hover:bg-teal-700 text-white"
            data-testid="new-patient-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Checkout · ${selectedPrice}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
